import {
  Box,
  Icon,
  Grid,
  HStack,
  Text,
  ThemeColorPath,
  ThemeColorTextPath,
  VStack,
} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useMemo} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useCatalogue} from '../../../../../hooks/useCatalogue';
import i18n from '../../../../../i18n/i18n';
import {EquipmentScoreType} from '../../../../../types/MarketType';
import {getMostSearchedEquipmentItems} from '../utils/getMostSearchedEquipmentItems';

interface MostSearchedEquipmentProps extends TestIdProps {
  equipmentScore?: EquipmentScoreType;
}

function getLabelColor(isAvailable: boolean) {
  return match<boolean, ThemeColorTextPath>(isAvailable)
    .with(true, () => 'primary')
    .with(false, () => 'danger')
    .exhaustive();
}

function getRowBackgroundColor(isAvailable: boolean) {
  return match<boolean, ThemeColorPath>(isAvailable)
    .with(false, () => 'palettes.red.10.100')
    .otherwise(() => 'general.transparent');
}

function getStatusIcon(isAvailable: boolean) {
  return match(isAvailable)
    .with(true, () => ({
      value: 'navigation/check' as const,
      color: 'severity.success' as const,
    }))
    .with(false, () => ({
      value: 'navigation/close' as const,
      color: 'severity.danger' as const,
    }))
    .exhaustive();
}

export function MostSearchedEquipment(props: MostSearchedEquipmentProps) {
  const {getFeatureLabel, hasFeatureTranslation} = useCatalogue();
  const mostSearchedEquipmentItems = useMemo(
    () =>
      getMostSearchedEquipmentItems(props.equipmentScore)
        .filter((item) => hasFeatureTranslation(item.featureCode))
        .map((item, index) => ({
          ...item,
          rank: index + 1,
          label: getFeatureLabel(item.featureCode),
        })),
    [props.equipmentScore, getFeatureLabel, hasFeatureTranslation]
  );

  return (
    <VStack spacing={6}>
      <HStack align="center" justify="center" spacing={2} wrap>
        <Text size="xSmall" color="secondary">
          {i18n.t('entity.overviewEquipment.labels.legend')}:
        </Text>
        <HStack align="center" spacing={1}>
          <Text size="xSmall" alternative>
            {i18n.t('entity.overviewEquipment.labels.availableFeature')}
          </Text>
          <Icon value="navigation/check" size={4} color="severity.success" />
        </HStack>
        <HStack align="center" spacing={1}>
          <Text size="xSmall" color="danger" alternative>
            {i18n.t('entity.overviewEquipment.labels.missingFeature')}
          </Text>
          <Icon value="navigation/close" size={4} color="severity.danger" />
        </HStack>
      </HStack>
      <Text size="xSmall" color="secondary">
        {i18n.t('entity.overviewEquipment.labels.featuresOrderedByMostSearched')}
      </Text>
      <Grid columns={[1, 1, 2]} spacing={0}>
        {mostSearchedEquipmentItems.map((item, index) => {
          const statusIcon = getStatusIcon(item.isAvailable);

          return (
            <Box
              key={item.featureCode}
              paddingHorizontal={4}
              backgroundColor={getRowBackgroundColor(item.isAvailable)}
              borderRight={[undefined, undefined, index % 2 === 0 ? '1px solid' : undefined]}
              borderBottom="1px solid"
              borderColor="palettes.neutral.20.100"
              data-testid={suffixTestId('mostSearchedEquipmentRow', props)}
            >
              <Box paddingVertical={1}>
                <HStack align="center" spacing={2}>
                  <Box width={6}>
                    <Text size="xSmall" color="tertiary" alternative>
                      {item.rank}
                    </Text>
                  </Box>
                  <HStack align="center" justify="space-between" spacing={2} grow={1}>
                    <Text size="xSmall" color={getLabelColor(item.isAvailable)} alternative>
                      {item.label}
                    </Text>
                    <Icon value={statusIcon.value} size={4} color={statusIcon.color} />
                  </HStack>
                </HStack>
              </Box>
            </Box>
          );
        })}
      </Grid>
    </VStack>
  );
}
